import { applyFilters, type FilterConfig, type FilterState, TableFilters } from '@/components/filter-bar';
import type { Jurisdiction } from '@/services/api/jurisdictions';
import { JURISDICTION_COLUMNS, type JurisdictionColumnConfig } from './jurisdiction-columns';

// Feature-scoped port of the legacy shared-table filter chips, now delegating chip
// rendering + matching to the promoted shared module.

/** Active filters keyed by column — see FilterState in src/components/filter-bar/types.ts */
export type JurisdictionFilterState = FilterState;

// The three boolean columns (allowedResidency/allowedCitizenship/allowedFiatTransfers) were
// `multi-text` in legacy, but text filters never match boolean cell values (exact `includes`
// against strings) so those filters were dead — ported as `multi-choice` boolean filters
// instead (deliberate fix), driven by their column config's static `filterOptions`
// (label/value pairs, e.g. { label: 'Yes', value: true }). getValue resolves the raw cell
// value to its configured LABEL so the shared FilterState (string[]-only, no separate
// label/value concept) and the dialog's checkbox text both read 'Yes'/'No' — the underlying
// match is still exact-equality membership either way, just keyed on the label string instead
// of the raw boolean.
const toFilterValue = (config: JurisdictionColumnConfig, row: Jurisdiction): unknown => {
  const raw = row[config.key];
  return config.filterOptions ? (config.filterOptions.find((option) => option.value === raw)?.label ?? raw) : raw;
};

/**
 * Legacy BopDataTable matching: scalar cells match by EXACT equality
 * (multi-text is not a substring search), array cells match when any
 * element is in the selection. Filters combine with AND. Matching itself is the
 * shared module's `applyFilters` — every column here defaults to matchMode 'exact'
 * (multi-text) or is `multi-choice` (always exact regardless of matchMode).
 */
const JURISDICTION_FILTER_CONFIGS: Array<FilterConfig<Jurisdiction>> = JURISDICTION_COLUMNS.filter((config) => config.filterable).map((config) => ({
  key: config.key,
  title: config.title,
  type: config.filterType === 'multi-text' ? 'multi-text' : 'multi-choice',
  getValue: (row: Jurisdiction) => toFilterValue(config, row),
  ...(config.filterOptions ? { options: config.filterOptions.map((option) => option.label) } : {}),
}));

export const applyJurisdictionFilters = (rows: Jurisdiction[], filters: JurisdictionFilterState): Jurisdiction[] => applyFilters(rows, JURISDICTION_FILTER_CONFIGS, filters);

interface JurisdictionFilterBarProps {
  data: Jurisdiction[];
  filters: JurisdictionFilterState;
  onFiltersChange: (filters: JurisdictionFilterState) => void;
}

export const JurisdictionFilterBar = ({ data, filters, onFiltersChange }: JurisdictionFilterBarProps) => (
  <TableFilters configs={JURISDICTION_FILTER_CONFIGS} onChange={onFiltersChange} rows={data} state={filters} />
);
JurisdictionFilterBar.displayName = 'JurisdictionFilterBar';
